import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Spinner } from '../../components/ui';

function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  const detail = candidate?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((entry) => {
        if (entry && typeof entry === 'object' && 'msg' in entry) {
          const msg = (entry as { msg?: unknown }).msg;
          return typeof msg === 'string' ? msg : JSON.stringify(entry);
        }
        return JSON.stringify(entry);
      })
      .join(' | ');
  }
  return fallback;
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { push } = useToasts();

  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isTokenMissing = !token.trim();
  const passwordsMatch = newPassword.trim().length > 0 && newPassword === confirmPassword;

  const tokenType = useMemo<string | null>(() => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload?.type === 'string' ? payload.type : null;
    } catch {
      return null;
    }
  }, [token]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isTokenMissing) {
      push({ kind: 'error', message: 'Password reset token is missing from this link' });
      return;
    }
    if (!passwordsMatch) {
      push({ kind: 'error', message: 'Passwords do not match' });
      return;
    }

    const endpoint =
      tokenType === 'self_serve_password_reset'
        ? '/v1/auth/password-reset/self-serve/confirm'
        : '/v1/auth/password-reset/confirm';

    setIsSubmitting(true);
    try {
      await AXIOS_INSTANCE.post(endpoint, {
        token,
        new_password: newPassword,
      });
      push({ kind: 'success', message: 'Password reset successful. Please sign in.' });
      navigate('/login', { replace: true });
    } catch (error) {
      push({ kind: 'error', message: getErrorMessage(error, 'Failed to reset password') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>Set a new password for your Eliza Accelerate account.</CardDescription>
        </CardHeader>
        <CardContent>
          {isTokenMissing ? (
            <div className="space-y-4">
              <p className="text-sm text-red-600">This reset link is invalid or incomplete.</p>
              <Link to="/login" className="text-sm text-eliza-red hover:underline">
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
              </div>
              {!passwordsMatch && confirmPassword.length > 0 ? (
                <p className="text-xs text-red-600">Passwords do not match.</p>
              ) : null}
              <Button type="submit" disabled={isSubmitting || !passwordsMatch} className="w-full">
                {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                Reset Password
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
